import React from "react";
import { Link } from "react-router-dom";

const CloudInfrastructureLayout = ({ service }) => {
    return (
        <div className="bg-white text-gray-900">

            {/* ================= HERO ================= */}
            <section
                className="relative min-h-[80vh] flex items-center justify-start"
                style={{
                    backgroundImage:
                        "url(/images/cloud/cloudhero.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60" />

                {/* LEFT-ALIGNED CONTENT */}
                <div className="relative w-full px-6 md:px-16 lg:px-24 py-24 text-white text-left">
                    <div className="max-w-3xl">
                        <h1 className="text-5xl md:text-6xl font-bold mb-6">
                            Cloud & Infrastructure Services
                        </h1>

                        <p className="text-xl text-gray-200 mb-8">
                            Accelerate innovation, improve resilience, and modernize enterprise
                            infrastructure with cloud-first transformation.
                        </p>

                        <Link
                            to="/contact"
                            className="inline-block bg-orange-500 hover:bg-orange-600 px-8 py-4 rounded-lg font-semibold text-white"
                        >
                            Talk to Our Experts
                        </Link>
                    </div>
                </div>
            </section>



            {/* ================= ANALYST STRIP ================= */}
            <section className="border-b">
                <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-6 text-center">
                    <div>
                        <h3 className="text-lg font-semibold">Industry Recognition</h3>
                        <p className="text-sm text-gray-600">
                            Trusted by global enterprises for cloud modernization
                        </p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold">Enterprise-Grade Delivery</h3>
                        <p className="text-sm text-gray-600">
                            Proven frameworks, accelerators, and IP
                        </p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold">Global Scale</h3>
                        <p className="text-sm text-gray-600">
                            Secure, compliant, and scalable solutions worldwide
                        </p>
                    </div>
                </div>
            </section>

            {/* ================= GARTNER STYLE INSIGHT ================= */}
            <section className="bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-4xl font-bold mb-6">
                            Cloud Transformation Insights
                        </h2>
                        <p className="text-gray-300 mb-6">
                            Enterprises are rethinking infrastructure to drive agility,
                            resilience, and cost efficiency. Our cloud-first strategy enables
                            continuous modernization.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                "Hybrid & Multi-Cloud",
                                "Cloud Native Platforms",
                                "FinOps Optimization",
                                "Secure by Design",
                            ].map((item) => (
                                <div
                                    key={item}
                                    className="bg-gray-800 p-4 rounded-lg text-sm"
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>
                    <img
                        src="/images/cloud/cloudinsights.png"
                        alt="Cloud strategy"
                        className="rounded-xl"
                    />
                </div>
            </section>

            {/* ================= CX / AI BLOCK ================= */}
            <section className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <h2 className="text-4xl font-bold mb-4">
                        AI-Driven, Cloud-Powered Enterprises
                    </h2>
                    <p className="max-w-3xl text-lg text-indigo-100">
                        Build intelligent platforms that scale with your business and
                        deliver superior customer and employee experiences.
                    </p>
                </div>
            </section>

            {/* ================= HYBRID & MULTI CLOUD ================= */}
            <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12">
                <div>
                    <h2 className="text-4xl font-bold mb-6">
                        Hybrid & Multi-Cloud Transformation
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Design, migrate, and manage complex hybrid and multi-cloud
                        environments with confidence.
                    </p>
                    <ul className="space-y-3 text-gray-700">
                        <li>✔ Cloud migration & modernization</li>
                        <li>✔ Kubernetes & container platforms</li>
                        <li>✔ Infrastructure as Code</li>
                        <li>✔ Resilience & disaster recovery</li>
                    </ul>
                </div>
                <img
                    src="https://www.kellerschroeder.com/wp-content/uploads/2023/08/Cloud-Solutions-2-Evansville-Nashville.png"
                    alt="Hybrid cloud"
                    className="rounded-xl"
                />
            </section>

            {/* ================= CLOUD PERSPECTIVE ================= */}
            <section className="bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <h2 className="text-4xl font-bold mb-12">
                        Our Cloud Transformation Perspective
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            "Business-Aligned Cloud Strategy",
                            "Platform Engineering Excellence",
                            "Security & Compliance First",
                        ].map((title) => (
                            <div
                                key={title}
                                className="bg-white p-8 rounded-xl shadow"
                            >
                                <h3 className="font-semibold text-lg mb-2">{title}</h3>
                                <p className="text-sm text-gray-600">
                                    Deliver measurable outcomes with scalable cloud platforms
                                    aligned to business goals.
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CLIENT SUCCESS ================= */}
            <section className="max-w-7xl mx-auto px-6 py-20">
                <h2 className="text-4xl font-bold mb-12">Client Success</h2>

                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        {
                            title: "Global Enterprise Cloud Modernization",
                            description:
                                "Reduced infrastructure costs by 40% while improving scalability across multi-region cloud environments."
                        },
                        {
                            title: "AI-Driven Infrastructure Optimization",
                            description:
                                "Implemented intelligent monitoring and automation, improving uptime to 99.99% and reducing incidents by 55%."
                        },
                        {
                            title: "Hybrid Cloud Migration for BFSI Client",
                            description:
                                "Successfully migrated mission-critical workloads to a secure hybrid cloud, accelerating deployment cycles by 3×."
                        }
                    ].map((item, i) => (
                        <div
                            key={i}
                            className="border rounded-xl p-6 hover:shadow-lg transition bg-white"
                        >
                            <h3 className="font-semibold mb-2 text-lg">
                                {item.title}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
            </section>


            {/* ================= PARTNERS ================= */}
            <section className="bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <h2 className="text-4xl font-bold mb-10">
                        Cloud Partner Ecosystem
                    </h2>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
                        {[
                            { name: "AWS", logo: "awscloud" },
                            { name: "Azure", logo: "azure" },
                            { name: "Google Cloud", logo: "googlecloud" },
                            { name: "IBM", logo: "ibm" },
                            { name: "Oracle", logo: "oracle" }
                        ].map((partner) => (
                            <div
                                key={partner.name}
                                className="flex items-center justify-center"
                            >
                                <img
                                    src={`/images/partners/${partner.logo}.png`}
                                    alt={partner.name}
                                    className="h-10 w-auto object-contain opacity-80 hover:opacity-100 transition"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* ================= WHY US ================= */}
            <section className="max-w-7xl mx-auto px-6 py-20">
                <h2 className="text-4xl font-bold mb-12">
                    Why TECHSPR for Cloud & Infrastructure
                </h2>
                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        "Deep cloud engineering expertise",
                        "Accelerators & reusable IP",
                        "Proven enterprise delivery",
                    ].map((item) => (
                        <div key={item} className="p-6 border rounded-xl">
                            <h3 className="font-semibold mb-2">{item}</h3>
                            <p className="text-sm text-gray-600">
                                Helping enterprises modernize faster with confidence.
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ================= CTA ================= */}
            <section className="bg-indigo-700 text-white">
                <div className="max-w-7xl mx-auto px-6 py-20 text-center">
                    <h2 className="text-4xl font-bold mb-4">
                        Ready to Modernize Your Cloud?
                    </h2>
                    <p className="text-indigo-100 mb-8">
                        Let’s build a resilient, scalable cloud foundation together.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-block bg-white text-indigo-700 px-8 py-4 rounded-lg font-semibold"
                    >
                        Contact Us
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default CloudInfrastructureLayout;
